import { Edit } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useHandleNurseChange } from "../../../../hooks/useHandleNurseChange";
import { INK } from "../../../../theme/tokens";
import type { CalledOffShift } from "../../../../utils/models";
import { getShiftIndexByShiftTimes } from "../../../../utils/utilFunctions";

type Props = {
	updateCallOff: (id: string, updatedFields: Partial<CalledOffShift>) => void;
	callOff: CalledOffShift;
};

export const EditRemovalMessage: FC<Props> = ({ updateCallOff, callOff }) => {
	const {
		removeNurse,
		isLoading: { removeNurse: removeNurseLoading },
	} = useHandleNurseChange();
	const { getWeekIndexByDate, getDayIndexByDate, showErrorSnackbar } = useHospitalData();
	const { track } = useAnalytics();
	const [editingRemovalMessageText, setEditingRemovalMessageText] = useState<string>(callOff.removalMessage ?? "");
	const [hovered, setHovered] = useState(false);

	const handleSaveRemovalMessage = useCallback(
		async (callOff: CalledOffShift) => {
			const newRemovalMessage = editingRemovalMessageText.trim();
			// Only save if the message has changed
			if (newRemovalMessage === (callOff.removalMessage ?? "")) {
				return;
			}

			try {
				const shiftIndex = getShiftIndexByShiftTimes({
					startTime: callOff.startTime,
					endTime: callOff.endTime,
				});
				const weekIndex = getWeekIndexByDate(callOff.startTime);
				const dayIndex = getDayIndexByDate(callOff.startTime);

				track("Updating removal message", {
					nurse_id: callOff.nurseId,
					shift_id: callOff.id,
				});

				await removeNurse({
					nurse: callOff,
					shiftIndex: shiftIndex,
					dayNo: dayIndex,
					weekIndex,
					message: callOff.message,
					removalMessage: newRemovalMessage || undefined,
				});

				updateCallOff(callOff.id, {
					removalMessage: newRemovalMessage || undefined,
				});

				track("Update removal message successfully", {
					nurse_id: callOff.nurseId,
					shift_id: callOff.id,
					removalMessage: newRemovalMessage,
				});
			} catch (e) {
				showErrorSnackbar("Failed to update removal message", { nurseId: callOff.nurseId, callOffId: callOff.id }, e);
				setEditingRemovalMessageText(callOff.message ?? "");
			} finally {
			}
		},
		[editingRemovalMessageText, getWeekIndexByDate, getDayIndexByDate, removeNurse, updateCallOff, showErrorSnackbar, track],
	);

	return (
		<SystemValidationTextField
			variant={"standard"}
			value={editingRemovalMessageText}
			onChange={(e) => setEditingRemovalMessageText(e.target.value)}
			inputSx={{
				px: 1,
			}}
			inputProps={{
				disableUnderline: !hovered,
				onMouseEnter: () => setHovered(true),
				onMouseLeave: () => setHovered(false),
				onBlur: () => handleSaveRemovalMessage(callOff),
				endAdornment: removeNurseLoading ? (
					<CircularProgress size={"12px"} />
				) : (
					<Edit
						className="edit-icon"
						sx={{
							fontSize: 16,
							color: INK.SECONDARY,
							opacity: hovered ? 0.7 : 0,
							marginLeft: "auto",
							transition: "opacity 0.2s ease",
						}}
					/>
				),
			}}
			onKeyDown={async (e) => {
				if (e.key === "Enter") {
					await handleSaveRemovalMessage(callOff);
				} else if (e.key === "Escape") {
					setEditingRemovalMessageText(callOff.removalMessage ?? "");
					await handleSaveRemovalMessage(callOff);
				}
			}}
			placeholder={"Add message..."}
			fullWidth
			reserveHeightForHelperText={false}
		/>
	);
};
